/**
 * Roster & roles. The roster is the single source of truth for who can approve what;
 * `syncRosterToUser` mirrors roles into users/{uid} (which the security rules read) and
 * into custom claims.
 */

import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useCollection, useDoc } from '../../lib/useFirestore';
import { useAuth } from '../../auth/AuthProvider';
import { db } from '../../firebase';
import { parseCsv, mapResponsiblePartnerRows, matchPracticeGroupText } from '../../lib/csv';
import { sortPeople } from '../../lib/sortPeople';
import DirectoryLookup from '../../components/DirectoryLookup';

const ROLES = [
  ['superadmin', 'Superadmin'],
  ['admin', 'Administrator'],
  ['managing_partner', 'Managing Partner'],
  ['general_counsel', 'General Counsel'],
  ['practice_group_leader', 'Practice Group Leader'],
  ['team_leader', 'Team Leader'],
  ['intake', 'Intake'],
  ['workflow_viewer', 'Workflow Viewer'],
  ['reminder_sender', 'Reminder Sender'],
  ['form_editor', 'Form Editor (question wording only)'],
  ['attorney', 'Attorney'],
  ['staff', 'Staff'],
];

const PRIVILEGED_ROLES = ['admin', 'superadmin'];

const cell = 'px-3 py-2 align-top';

export default function AdminRoster() {
  const { isSuperAdmin, isAdmin } = useAuth();
  const roster = useCollection('roster');
  const groups = useCollection('practiceGroups');
  const { run, busy, error, saved } = useCallable();
  const [editing, setEditing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const editPanelRef = useRef(null);

  // Scrolling the edit panel into view has to happen after the panel actually exists in
  // the DOM. flushSync forces React to commit the new `editing` state synchronously
  // before scrollIntoView runs, so this works reliably even when "Edit" is clicked on a
  // row far below the fold and the panel wasn't previously mounted at all.
  const openEditor = (profile) => {
    flushSync(() => setEditing(profile));
    editPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const startNew = (seed = {}) =>
    openEditor({
      uid: '',
      displayName: '',
      email: '',
      employeeCode: '',
      roles: ['attorney'],
      practiceGroupIds: [],
      isActive: true,
      ...seed,
    });

  /**
   * A directory result, turned into the start of a roster record.
   *
   * Name, address and title only. Roles and practice groups are deliberately NOT taken from the
   * directory: a job title describes somebody's job, while a role here decides who may approve a
   * matter, and reading one into the other would let an HR title change quietly alter who can sign
   * off on work. The default role is the same one a blank record starts with.
   */
  const startFromDirectory = (person) =>
    startNew({
      displayName: person.displayName,
      email: person.email,
      title: person.jobTitle || '',
    });

  const save = async () => {
    const { uid, ...profile } = editing;
    await run('adminUpsertRosterUser', {
      uid: uid || profile.email.toLowerCase().replace(/[^a-z0-9]/g, '_'),
      profile,
    });
    setEditing(null);
  };

  return (
    <div className="space-y-4">
      <DirectoryLookup
        existingEmails={(roster.data || []).map((p) => p.email)}
        onPick={startFromDirectory}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => startNew()}
          className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400"
        >
          Add person
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      {editing && (
        <div ref={editPanelRef} className="grid gap-3 rounded-lg bg-white dark:bg-slate-900 p-4 shadow sm:grid-cols-2">
          <label className="text-sm">
            Name
            <input
              className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
              value={editing.displayName}
              onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Email
            <input
              className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
              value={editing.email}
              onChange={(e) => setEditing({ ...editing, email: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Employee code
            <input
              className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
              value={editing.employeeCode || ''}
              onChange={(e) => setEditing({ ...editing, employeeCode: e.target.value })}
            />
          </label>
          <fieldset className="sm:col-span-2">
            <legend className="text-sm">Roles</legend>
            {!isSuperAdmin && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                Only a superadmin can grant or revoke Administrator/Superadmin access. You can
                still assign every other role below.
              </p>
            )}
            <div className="mt-1 flex flex-wrap gap-3">
              {ROLES.map(([key, label]) => {
                const locked = PRIVILEGED_ROLES.includes(key) && !isSuperAdmin;
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-1 text-sm ${locked ? 'opacity-40' : ''}`}
                  >
                    <input
                      type="checkbox"
                      disabled={locked}
                      checked={editing.roles.includes(key)}
                      onChange={() =>
                        setEditing({
                          ...editing,
                          roles: editing.roles.includes(key)
                            ? editing.roles.filter((r) => r !== key)
                            : [...editing.roles, key],
                        })
                      }
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <fieldset className="sm:col-span-2">
            <legend className="text-sm">Practice groups</legend>
            <div className="mt-1 flex flex-wrap gap-3">
              {groups.data.map((g) => (
                <label key={g.id} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={editing.practiceGroupIds.includes(g.id)}
                    onChange={() =>
                      setEditing({
                        ...editing,
                        practiceGroupIds: editing.practiceGroupIds.includes(g.id)
                          ? editing.practiceGroupIds.filter((id) => id !== g.id)
                          : [...editing.practiceGroupIds, g.id],
                      })
                    }
                  />
                  {g.name}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={editing.isActive}
              onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
            />
            Active (unchecking this removes them from every roster picker/search on the
            form, from approver resolution, and hides them from this list by default —
            use “Show inactive” above to bring them back or reactivate them)
          </label>
          <div className="flex gap-2 sm:col-span-2">
            <button
              type="button"
              onClick={save}
              disabled={busy || !editing.email}
              className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {(() => {
        /**
         * A superseded row is not a person, it is the husk of one.
         *
         * When someone signs in for the first time their roster row is re-keyed from the id the
         * CSV import derives to the uid Auth minted, and the original is kept so that any matter
         * naming the old uid still resolves. It is not a separate colleague and must never be
         * offered as one - not even under 'Show inactive', where it would read as a real person
         * who had left.
         */
        const people = roster.data.filter((p) => !p.supersededByUid);
        const inactiveCount = people.filter((p) => p.isActive === false).length;
        const activeScoped = showInactive ? people : people.filter((p) => p.isActive !== false);
        const searched = search.trim()
          ? activeScoped.filter((p) => {
              const q = search.trim().toLowerCase();
              return (
                (p.displayName || '').toLowerCase().includes(q) ||
                (p.email || '').toLowerCase().includes(q) ||
                (p.employeeCode || '').toLowerCase().includes(q)
              );
            })
          : activeScoped;
        const visibleRows = sortPeople(searched);
        return (
          <>
            {inactiveCount > 0 && (
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                Show inactive ({inactiveCount} hidden — e.g. bad entries from the automated
                roster feed. Mark someone inactive below to remove them from this list and
                from every approver/roster picker on the form; toggle this on to bring them
                back into view or reactivate them.)
              </label>
            )}
            <label className="block text-xs text-slate-600 dark:text-slate-300">
              Search name / email / employee code
              <input
                type="text"
                className="mt-1 block w-56 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <div className="max-h-[70vh] overflow-auto rounded-lg bg-white dark:bg-slate-900 shadow">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className={cell}>Name</th>
                  <th className={cell}>Email</th>
                  <th className={cell}>Emp. code</th>
                  <th className={cell}>Roles</th>
                  <th className={cell}>Practice groups</th>
                  <th className={cell}>Active</th>
                  <th className={cell} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {visibleRows.map((person) => (
                  <tr key={person.id} className={person.isActive === false ? 'opacity-50' : ''}>
                    <td className={cell}>{person.displayName}</td>
                    <td className={cell}>{person.email}</td>
                    <td className={cell}>
                      {person.employeeCode ? (
                        <code className="text-xs text-slate-400 dark:text-slate-500">{person.employeeCode}</code>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500">—</span>
                      )}
                    </td>
                    <td className={cell}>{(person.roles || []).join(', ')}</td>
                    <td className={cell}>{(person.practiceGroupIds || []).join(', ') || '—'}</td>
                    <td className={cell}>{person.isActive === false ? 'No' : 'Yes'}</td>
                    <td className={cell}>
                      <button
                        type="button"
                        className="text-firm-700 dark:text-firm-100 hover:underline"
                        onClick={() =>
                          openEditor({
                            uid: person.id,
                            displayName: person.displayName || '',
                            email: person.email || '',
                            employeeCode: person.employeeCode || '',
                            roles: person.roles || [],
                            practiceGroupIds: person.practiceGroupIds || [],
                            isActive: person.isActive !== false,
                          })
                        }
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td className={`${cell} text-slate-400 dark:text-slate-500`} colSpan={7}>
                      No one to show. Add a person, or check “Show inactive” above.
                    </td>
                  </tr>
                )}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}

      <RosterFeedStatus />
      <ResponsiblePartnerImport groups={groups.data} />
      <FirmLeadership roster={roster.data} />
      <PracticeGroups groups={groups.data} roster={roster.data} />
    </div>
  );
}

/**
 * Short, human-readable summary of a pollRosterFeed() outcome object, for the "Poll
 * last ran" line — shared between whatever the scheduled 6am run or a manual "Run poll
 * now" click last recorded to system/rosterFeedPollStatus.
 */
function summarizePollOutcome(outcome) {
  if (!outcome) return '';
  if (outcome.ok === false) return `error: ${outcome.error || 'unknown error'}`;
  if (outcome.skipped) {
    return outcome.skipped === 'graph_not_configured' ? 'skipped (mail not configured)' : 'skipped (emulator)';
  }
  if (outcome.imported) return `found ${outcome.found ?? 1}, imported`;
  if (outcome.alreadyProcessed) {
    // scanned/skippedMessageIds distinguish "every candidate Graph returned was already
    // claimed" (real, correct dedupe) from a bare boolean that used to give no way to
    // tell whether more than one candidate even existed to check.
    const scanned = outcome.scanned ?? outcome.found ?? 0;
    const skippedCount = outcome.skippedMessageIds?.length ?? scanned;
    return scanned > 1
      ? `found ${scanned}, all ${skippedCount} already processed`
      : `found ${scanned}, already processed`;
  }
  return `found ${outcome.found ?? 0}`;
}

/**
 * Status panel for the automated SSRS "NMMExport" email import (functions/src/roster/
 * rosterFeed.js), which polls the shared mailbox daily at 6am for the report that lands
 * around 3am and imports it through the same server-side path as the manual upload
 * below. Shows the most recent processed message from rosterFeedLog, the timestamp of
 * the poll's own last execution (regardless of whether it found anything), and a
 * button to run the poll immediately, for testing without waiting for the schedule.
 */
function RosterFeedStatus() {
  const poller = useCallable();
  const clearer = useCallable();
  const [lastRun, setLastRun] = useState(null);
  const [clearedId, setClearedId] = useState(null);
  const log = useCollection('rosterFeedLog', [['orderBy', 'claimedAt', 'desc']]);
  // "Poll last ran at" — distinct from the "Last processed email" section below. This
  // records every pollRosterFeed() invocation (scheduled or manual), whether or not it
  // found/imported anything, so an admin can tell "the poll ran today but found nothing
  // new" apart from "the poll hasn't run today yet" (see rosterFeed.js's
  // recordPollStatus()).
  const pollStatus = useDoc('system/rosterFeedPollStatus');

  useEffect(() => {
    if (log.data.length > 0) setLastRun(log.data[0]);
  }, [log.data]);

  const runNow = async () => {
    const outcome = await poller.run('adminRunRosterFeedPoll', {});
    if (outcome) setLastRun((prev) => ({ ...prev, _manualRunOutcome: outcome }));
  };

  // Only offered for a non-'imported' status: a stuck 'processing' claim (the run died
  // before it could record an outcome) or a recorded 'error'. A genuinely 'imported'
  // entry is real, correct dedupe and should not normally be cleared from here —
  // claimMessage() already protects it from casual reprocessing on the poll side too.
  const clearStuckClaim = async (messageId) => {
    const outcome = await clearer.run('adminClearRosterFeedClaim', { messageId });
    if (outcome?.cleared) setClearedId(messageId);
  };

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Automated roster feed (SSRS email)</h2>
        <CallStatus busy={poller.busy} error={poller.error} saved={false} />
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Every morning at 6am, the app checks the shared mailbox for that day's SSRS
        "NMMExport" report (sent automatically around 3am) and imports it — no manual
        upload needed. The manual upload below still works any time, e.g. to re-run an
        import early or recover from a missed email.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={runNow}
          disabled={poller.busy}
          className="rounded border border-firm-700 dark:border-firm-500 px-3 py-1.5 text-sm font-medium text-firm-700 dark:text-firm-100 hover:bg-firm-50 dark:hover:bg-firm-900 disabled:opacity-50"
        >
          Run poll now
        </button>
        {poller.error && <span className="text-xs text-red-600 dark:text-red-400">{poller.error}</span>}
      </div>

      {!pollStatus.loading && pollStatus.data?.ranAt && (
        <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">
          Poll last ran:{' '}
          <span className="font-medium">
            {new Date(
              pollStatus.data.ranAt.toDate ? pollStatus.data.ranAt.toDate() : pollStatus.data.ranAt
            ).toLocaleString()}
          </span>{' '}
          <span className="text-slate-400 dark:text-slate-500">
            ({pollStatus.data.trigger === 'manual' ? 'manual run' : 'scheduled'})
          </span>
          {' — '}
          <span
            className={
              pollStatus.data.outcome?.imported
                ? 'text-emerald-700 dark:text-emerald-300'
                : pollStatus.data.outcome?.ok === false
                ? 'text-red-700 dark:text-red-300'
                : 'text-amber-700 dark:text-amber-300'
            }
          >
            {summarizePollOutcome(pollStatus.data.outcome)}
          </span>
        </p>
      )}

      {lastRun?._manualRunOutcome && (
        <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 text-xs text-slate-700 dark:text-slate-200">
          {lastRun._manualRunOutcome.imported && (
            <p>
              Imported just now: {lastRun._manualRunOutcome.importResult?.created ?? 0} created,{' '}
              {lastRun._manualRunOutcome.importResult?.updated ?? 0} updated
              {lastRun._manualRunOutcome.importResult?.deactivated?.length > 0
                ? `, ${lastRun._manualRunOutcome.importResult.deactivated.length} deactivated`
                : ''}.
              {lastRun._manualRunOutcome.skippedMessageIds?.length > 0 && (
                <span className="text-slate-500 dark:text-slate-400">
                  {' '}
                  ({lastRun._manualRunOutcome.skippedMessageIds.length} other already-processed email
                  {lastRun._manualRunOutcome.skippedMessageIds.length === 1 ? '' : 's'} in the mailbox were left
                  alone.)
                </span>
              )}
            </p>
          )}
          {lastRun._manualRunOutcome.imported &&
            lastRun._manualRunOutcome.importResult?.deactivated?.length > 0 && (
              <div className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-2">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                  {lastRun._manualRunOutcome.importResult.deactivated.length}{' '}
                  {lastRun._manualRunOutcome.importResult.deactivated.length === 1 ? 'person' : 'people'} marked
                  inactive — not present in this CSV. They will no longer appear in active dropdowns.
                </p>
                <ul className="mt-1 list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">
                  {lastRun._manualRunOutcome.importResult.deactivated.slice(0, 20).map((d, i) => (
                    <li key={i}>
                      {d.displayName || d.uid} ({d.email || 'no email'})
                    </li>
                  ))}
                </ul>
                {lastRun._manualRunOutcome.importResult.deactivated.length > 20 && (
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                    and {lastRun._manualRunOutcome.importResult.deactivated.length - 20} more
                  </p>
                )}
              </div>
            )}
          {!lastRun._manualRunOutcome.imported && lastRun._manualRunOutcome.found === 0 && (
            <p>No unread NMMExport email found in the mailbox right now.</p>
          )}
          {!lastRun._manualRunOutcome.imported && lastRun._manualRunOutcome.alreadyProcessed && (
            <p>
              {(lastRun._manualRunOutcome.scanned ?? lastRun._manualRunOutcome.found ?? 0) > 1
                ? `Checked ${lastRun._manualRunOutcome.scanned ?? lastRun._manualRunOutcome.found} NMMExport emails in the mailbox — all of them were already imported by an earlier run.`
                : 'The NMMExport email was already imported by an earlier run.'}
            </p>
          )}
          {!lastRun._manualRunOutcome.ok && (
            <p className="text-red-700 dark:text-red-300">Poll error: {lastRun._manualRunOutcome.error}</p>
          )}
          {lastRun._manualRunOutcome.skipped && (
            <p>
              Skipped —{' '}
              {lastRun._manualRunOutcome.skipped === 'graph_not_configured'
                ? 'mail is not configured yet.'
                : 'running in the emulator.'}
            </p>
          )}
        </div>
      )}

      {log.loading && <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">Loading last run…</p>}
      {!log.loading && log.data.length === 0 && (
        <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">No automated import has run yet.</p>
      )}
      {!log.loading && log.data.length > 0 && (
        <div className="mt-3 text-xs text-slate-600 dark:text-slate-300">
          <p>
            Last processed email: <span className="font-medium">{log.data[0].subject}</span>{' '}
            received {log.data[0].receivedAt ? new Date(log.data[0].receivedAt).toLocaleString() : '—'}
          </p>
          <p className="mt-1">
            Status:{' '}
            <span
              className={
                log.data[0].status === 'imported'
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : log.data[0].status === 'error'
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-amber-700 dark:text-amber-300'
              }
            >
              {log.data[0].status}
            </span>
            {log.data[0].detail ? ` — ${log.data[0].detail}` : ''}
          </p>
          {(log.data[0].status === 'error' || log.data[0].status === 'processing') && (
            <div className="mt-2">
              {clearedId === log.data[0].id ? (
                <p className="text-emerald-700 dark:text-emerald-300">
                  Cleared — the next poll will treat this email as new.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => clearStuckClaim(log.data[0].id)}
                  disabled={clearer.busy}
                  className="rounded border border-amber-600 dark:border-amber-500 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950 disabled:opacity-50"
                >
                  {log.data[0].status === 'processing'
                    ? 'Clear stuck claim (let this email be reprocessed)'
                    : 'Clear failed claim (let this email be retried)'}
                </button>
              )}
              {clearer.error && <p className="mt-1 text-red-600 dark:text-red-400">{clearer.error}</p>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Manual CSV upload for the daily SSRS "Responsible Partners" report. This remains
 * available at any time as a backstop for the automated email import above — e.g. to
 * re-run an import early, or recover if a day's NMMExport email is ever missed, delayed,
 * or malformed. Both paths call the exact same server-side import logic, so results are
 * identical either way.
 */
function ResponsiblePartnerImport({ groups }) {
  const importer = useCallable();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null); // { rows, invalidCount, unmatchedGroupCount }
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    setResult(null);
    setPreview(null);
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    const { headers, rows } = parseCsv(text);
    const mapped = mapResponsiblePartnerRows(headers, rows);
    const invalidCount = mapped.filter((r) => !r.email || !r.email.includes('@')).length;
    const unmatchedGroupCount = mapped.filter(
      (r) => r.practiceGroup && !matchPracticeGroupText(r.practiceGroup, groups)
    ).length;
    setPreview({ rows: mapped, invalidCount, unmatchedGroupCount });
  };

  const doImport = async () => {
    if (!preview?.rows?.length) return;
    const outcome = await importer.run('adminBulkImportResponsiblePartners', { rows: preview.rows });
    if (outcome) {
      setResult(outcome);
      setPreview(null);
      setFileName('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Import responsible partners (CSV)</h2>
        <CallStatus busy={importer.busy} error={importer.error} saved={false} />
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Upload the daily SSRS export (Name, Email, Practice Group columns — header names are
        matched loosely). Each row is upserted into the roster as an active attorney and
        linked to a matching practice group, so they appear in the Responsible Partner
        picker on the intake form. Re-uploading is safe — existing people are matched and
        updated by email, not duplicated.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          className="text-sm"
        />
        {fileName && <span className="text-xs text-slate-500 dark:text-slate-400">{fileName}</span>}
        {preview && (
          <button
            type="button"
            onClick={doImport}
            disabled={importer.busy || preview.rows.length === 0}
            className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
          >
            Import {preview.rows.length} row{preview.rows.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {preview && (
        <div className="mt-3">
          {preview.invalidCount > 0 && (
            <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
              {preview.invalidCount} row{preview.invalidCount === 1 ? '' : 's'} missing a valid
              email and will be skipped.
            </p>
          )}
          {preview.unmatchedGroupCount > 0 && (
            <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
              {preview.unmatchedGroupCount} row{preview.unmatchedGroupCount === 1 ? '' : 's'}{' '}
              reference a practice group that doesn’t exist yet (or matches more than one
              existing group) — those attorneys will still be imported, just without a
              practice group link. Create the missing group first, or fix the name, and
              re-upload to link them.
            </p>
          )}
          <div className="max-h-56 overflow-auto rounded border border-slate-200 dark:border-slate-700">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                <tr>
                  <th className={cell}>Name</th>
                  <th className={cell}>Email</th>
                  <th className={cell}>Practice group</th>
                </tr>
              </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {preview.rows.slice(0, 200).map((r, i) => {
                  const emailInvalid = !r.email || !r.email.includes('@');
                  const groupUnmatched = r.practiceGroup && !matchPracticeGroupText(r.practiceGroup, groups);
                  return (
                    <tr key={i} className={emailInvalid || groupUnmatched ? 'bg-amber-50 dark:bg-amber-950' : ''}>
                      <td className={cell}>{r.name || <span className="text-slate-400 dark:text-slate-500">—</span>}</td>
                      <td className={cell}>{r.email || <span className="text-slate-400 dark:text-slate-500">—</span>}</td>
                      <td className={cell}>
                        {r.practiceGroup ? (
                          <span className={groupUnmatched ? 'text-amber-700 dark:text-amber-300' : ''}>
                            {r.practiceGroup}
                            {groupUnmatched ? ' (no match)' : ''}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 200 && (
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">Showing first 200 of {preview.rows.length} rows.</p>
          )}
        </div>
      )}

      {result && (
        <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 text-sm">
          <p className="font-medium text-slate-800 dark:text-slate-100">
            Imported: {result.created} created, {result.updated} updated
            {result.deactivated?.length > 0 ? `, ${result.deactivated.length} deactivated` : ''}.
          </p>
          {result.deactivated?.length > 0 && (
            <div className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-2">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                {result.deactivated.length} {result.deactivated.length === 1 ? 'person' : 'people'} marked inactive:
              </p>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                Not present in this CSV — marked inactive. They will no longer appear in active
                dropdowns.
              </p>
              <ul className="mt-1 list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">
                {result.deactivated.map((d, i) => (
                  <li key={i}>{d.displayName || d.uid} ({d.email || 'no email'})</li>
                ))}
              </ul>
            </div>
          )}
          {result.skipped?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">Skipped rows:</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-amber-700 dark:text-amber-300">
                {result.skipped.map((s, i) => (
                  <li key={i}>Row {s.row}: {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-red-700 dark:text-red-300">Warnings:</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-red-700 dark:text-red-300">
                {result.errors.map((e, i) => (
                  <li key={i}>Row {e.row}: {e.reason}</li>
                ))}
              </ul>
            </div>
          )}
          {result.pglUpdates?.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Practice group leader(s) updated:</p>
              <ul className="mt-1 list-disc pl-5 text-xs text-emerald-700 dark:text-emerald-300">
                {result.pglUpdates.map((u, i) => (
                  <li key={i}>{u.practiceGroupName} → {u.pglName}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Firm-wide leadership. Managing Partner is stored on settings/approvalFlow
 * (managingPartnerUid) — same field the approval-flow editor and resolver use —
 * surfaced here too since this is where admins naturally look for firm structure.
 */
function FirmLeadership({ roster }) {
  const { data: stored, loading } = useDoc('settings/approvalFlow');
  const { run, busy, error, saved } = useCallable();
  const seeder = useCallable();
  const [mpUid, setMpUid] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (stored && !touched) setMpUid(stored.managingPartnerUid || '');
  }, [stored, touched]);

  const managingPartner = roster.find((u) => u.id === mpUid);

  // `settings/approvalFlow` only exists once someone has saved the Approval Flow editor
  // or run the one-time seed. On a fresh production project it can be completely
  // missing (the emulator-only seed script never ran against production), in which
  // case `stored` is null and this Save button used to silently do nothing. Now it
  // seeds sane defaults first (idempotent, additive-only), re-reads the freshly-created
  // doc, then saves on top of it — so Save always works instead of no-op'ing.
  const save = async () => {
    let base = stored;
    if (!base) {
      const outcome = await seeder.run('adminSeedDefaultSettings', {});
      if (!outcome) return; // seeder surfaced its own error via CallStatus below
      const snap = await getDoc(doc(db, 'settings/approvalFlow'));
      base = snap.exists() ? snap.data() : {};
    }
    await run('adminUpdateSettings', {
      docId: 'approvalFlow',
      value: { ...base, managingPartnerUid: mpUid || null },
    });
    setTouched(false);
  };

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Firm leadership</h2>
        <CallStatus busy={busy || seeder.busy} error={error || seeder.error} saved={saved} />
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        The Managing Partner is the firm-wide fallback approver used by the “Managing Partner”
        and “Managing Partner OR PGL” approval steps.
      </p>
      {!loading && !stored && (
        <p className="mt-2 rounded bg-amber-50 dark:bg-amber-950 px-2 py-1 text-xs text-amber-800 dark:text-amber-200">
          Workflow settings haven’t been created yet. Saving here will set them up with
          defaults automatically.
        </p>
      )}
      {loading ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            Managing Partner
            <select
              className="mt-1 block w-64 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
              value={mpUid}
              onChange={(e) => {
                setMpUid(e.target.value);
                setTouched(true);
              }}
            >
              <option value="">— resolve from roles —</option>
              {sortPeople(roster).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName}
                </option>
              ))}
            </select>
          </label>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {managingPartner ? (
              <>
                <span className="font-medium text-slate-800 dark:text-slate-100">{managingPartner.displayName}</span>
                {' · '}
                {managingPartner.email}
              </>
            ) : (
              <span className="text-slate-400 dark:text-slate-500">No specific person set — falls back to whoever holds the Managing Partner role.</span>
            )}
          </p>
          <button
            type="button"
            onClick={save}
            disabled={busy || !touched}
            className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            Save
          </button>
        </div>
      )}
    </section>
  );
}

function PracticeGroups({ groups, roster }) {
  const { run, busy, error, saved } = useCallable();
  const [newGroup, setNewGroup] = useState({ name: '', code: '' });

  const setField = (group, field, value) =>
    run('adminUpsertPracticeGroup', { groupId: group.id, group: { [field]: value || null } });

  const addGroup = async () => {
    if (!newGroup.name.trim()) return;
    const groupId = newGroup.name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
    await run('adminUpsertPracticeGroup', {
      groupId,
      group: {
        name: newGroup.name.trim(),
        code: newGroup.code.trim() || null,
        practiceGroupLeaderUid: null,
        teamLeaderUid: null,
        isActive: true,
      },
    });
    setNewGroup({ name: '', code: '' });
  };

  const personLabel = (uid) => {
    const person = roster.find((u) => u.id === uid);
    return person ? `${person.displayName} · ${person.email}` : null;
  };

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-4 shadow">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Practice groups</h2>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        The Practice Group Leader here is who the standard PGL approval step resolves to. A
        group with no Team Leader falls back to its PGL.
      </p>
      <table className="mt-3 w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <tr>
            <th className={cell}>Group</th>
            <th className={cell}>Practice Group Leader</th>
            <th className={cell}>Team Leader</th>
          </tr>
        </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {groups.map((group) => (
            <tr key={group.id}>
              <td className={cell}>
                <input
                  className="w-full rounded border border-transparent px-2 py-1 font-medium text-slate-800 dark:text-slate-100 hover:border-slate-300 dark:hover:border-slate-600 focus:border-slate-300 dark:focus:border-slate-600"
                  defaultValue={group.name}
                  onBlur={(e) => {
                    if (e.target.value.trim() && e.target.value.trim() !== group.name) {
                      setField(group, 'name', e.target.value.trim());
                    }
                  }}
                />
              </td>
              {['practiceGroupLeaderUid', 'teamLeaderUid'].map((field) => (
                <td key={field} className={cell}>
                  <select
                    className="w-full rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
                    value={group[field] || ''}
                    onChange={(e) => setField(group, field, e.target.value)}
                  >
                    <option value="">— none —</option>
                    {sortPeople(roster).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                  {personLabel(group[field]) && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{personLabel(group[field])}</p>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-3">
        <label className="text-xs text-slate-600 dark:text-slate-300">
          New practice group name
          <input
            className="mt-1 block w-64 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            placeholder="e.g. Real Estate Practice Group"
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
          />
        </label>
        <label className="text-xs text-slate-600 dark:text-slate-300">
          Code (optional)
          <input
            className="mt-1 block w-24 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
            placeholder="REPG"
            value={newGroup.code}
            onChange={(e) => setNewGroup({ ...newGroup, code: e.target.value })}
          />
        </label>
        <button
          type="button"
          onClick={addGroup}
          disabled={busy || !newGroup.name.trim()}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40"
        >
          Add practice group
        </button>
      </div>
    </section>
  );
}
